import { getExtractionDetail } from './procedural-helpers';
import type { ExtractionPhase, ExtractionState } from '@/api/hooks/useProceduralStream';
import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

interface ProceduralExtractionLoaderProps {
  state: ExtractionState;
}

type StageState = 'completed' | 'active' | 'upcoming';

interface StageDescriptor {
  key: Exclude<ExtractionPhase, 'failed' | 'done'>;
  title: string;
  description: string;
}

const STAGES: StageDescriptor[] = [
  { key: 'reading', title: 'Reading', description: 'Scanning for procedural dates and events.' },
  { key: 'extracting', title: 'Extracting', description: 'Pulling out key deadlines and events.' },
  { key: 'merging', title: 'Reviewing', description: 'Ordering events and resolving conflicts.' },
];

const STAGE_INDEX: Record<Exclude<ExtractionPhase, 'failed'>, number> = {
  reading: 0,
  extracting: 1,
  merging: 2,
  done: 3,
};

// Dot-grid that surrounds each card. The card itself (bg-white) masks the centre, so the
// dots only show in the padded ring around it — strongest right next to the card and
// fading out towards the edges via the radial mask. Colour is driven by the card state.
const DotGridColors: Partial<Record<StageState, string>> = {
  active: 'rgba(56, 189, 248, 0.9)',
};

const DotGrid = ({ state }: { state: StageState }) => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0"
    style={{
      backgroundImage: `radial-gradient(${DotGridColors[state] ?? 'rgba(148, 163, 184, 0.55)'} 1px, transparent 1px)`,
      backgroundSize: '8px 8px',
      maskImage: 'radial-gradient(ellipse 55% 55% at center, black 70%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(ellipse 55% 55% at center, black 70%, transparent 100%)',
    }}
  />
);

const StageStatusConfig: Record<StageState, { icon: string | null; label: string; textColor: string }> = {
  completed: { icon: 'lucide--circle-check', label: 'Complete', textColor: 'text-teal-500' },
  active: { icon: 'lucide--loader animate-spin ease-in-out', label: 'In progress', textColor: 'text-sky-500' },
  upcoming: { icon: null, label: 'Pending', textColor: 'text-gray-400' },
};

export function ProceduralExtractionLoader({ state }: ProceduralExtractionLoaderProps) {
  // Failures are surfaced via toast; callers fall through to the empty state.
  if (state.phase === 'failed') return null;
  const detail = getExtractionDetail(state);
  const currentIndex = STAGE_INDEX[state.phase];

  const getState = (index: number): StageState => {
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'active';
    return 'upcoming';
  };

  return (
    <div className="mt-32 flex flex-col items-center">
      <div className="text-center">
        <h2 className="font-serif text-3xl font-medium">Extracting your timetable...</h2>
        <p className="mt-2 text-sm text-gray-600">Once processing finishes, your procedural events will appear here.</p>
        {detail !== null && <p className="mt-3 text-xs text-gray-500">{detail}</p>}
      </div>

      <div className="mt-12 grid w-full max-w-6xl grid-cols-1 items-center gap-4 px-6 sm:grid-cols-3">
        {STAGES.map((stage, index) => {
          const stageState = getState(index);

          return (
            <div key={stage.key} className="relative h-fit p-6">
              <DotGrid state={stageState} />

              <div
                className={cn(
                  'relative flex h-full flex-col rounded-xl border transition-all',
                  stageState === 'active' && 'border-sky-300 bg-sky-50 shadow-[0_0_32px_rgba(56,189,248,0.25)]',
                  stageState === 'completed' && 'border-teal-300 bg-teal-50',
                  stageState === 'upcoming' && 'border-gray-200 bg-gray-100',
                )}
              >
                <div className="flex flex-1 flex-col gap-1 rounded-xl bg-white p-4 px-5">
                  <p className="text-sm font-semibold text-gray-800">
                    <span className="font-normal text-gray-400">{index + 1}.</span> {stage.title}
                  </p>
                  <p className="text-xs leading-snug text-gray-500">{stage.description}</p>
                </div>

                <div
                  className={cn(
                    'flex items-center gap-1.5 rounded-b-xl px-4 py-2.5 text-xs font-medium',
                    stageState === 'active' && 'bg-sky-50',
                    stageState === 'completed' && 'bg-teal-50',
                    stageState === 'upcoming' && 'bg-gray-100',
                    StageStatusConfig[stageState].textColor,
                  )}
                >
                  {StageStatusConfig[stageState].icon && <Icon className={StageStatusConfig[stageState].icon} />}
                  {StageStatusConfig[stageState].label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
